/**
 * The live-connection banner over the rundown editor: nothing while live,
 * a quiet note while first connecting, and a clear warning while the socket
 * is reconnecting — others' changes are not arriving, and are caught up
 * (`changedSince`) once it is back.
 */
import { WifiOffIcon } from 'lucide-react';

export type ReconnectBannerProps = {
  readonly connection: 'connecting' | 'live' | 'reconnecting';
  /** When the next attempt is due, ms, if known. */
  readonly retryInMs: number | null;
  /** Local edits not yet confirmed by the server. */
  readonly pending: number;
};

export function ReconnectBanner({ connection, retryInMs, pending }: ReconnectBannerProps) {
  if (connection === 'live') return null;
  if (connection === 'connecting') {
    return (
      <div role="status" data-testid="connecting-banner" className="border-b bg-muted px-4 py-1 text-xs text-muted-foreground">
        Connecting to live updates…
      </div>
    );
  }
  const retry = retryInMs === null ? '' : ` Next attempt in ${String(Math.max(1, Math.round(retryInMs / 1000)))} s.`;
  return (
    <div role="alert" data-testid="reconnect-banner" className="flex items-center gap-2 border-b border-warning bg-warning px-4 py-1.5 text-sm text-warning-foreground">
      <WifiOffIcon className="size-4 shrink-0" aria-hidden="true" />
      <span>
        <strong>Reconnecting…</strong> Live updates from others are paused and will catch up when the connection is back.{retry}
        {pending > 0 ? ` ${String(pending)} of your edits waiting for the server.` : ''}
      </span>
    </div>
  );
}
